import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';

export interface ClubPayment {
  id: string;
  userId: string;
  clubId: string;
  clubName: string;
  amount: number;       // in INR
  currency: 'INR';
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  paidAt?: string;
  createdAt: string;
  // Populated on admin endpoint
  user?: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
    phoneNumber?: string;
  };
}

export interface RazorpayOrder {
  orderId: string;   // Razorpay order_id
  amount: number;    // amount in paise (INR × 100)
  currency: 'INR';
  keyId: string;     // Razorpay public key
  clubName: string;
  description: string;
  prefillEmail?: string;
  prefillContact?: string;
  prefillName?: string;
}

// Initiate a Razorpay payment for a paid club (called after admin approval)
export async function initiateClubPayment(clubId: string): Promise<RazorpayOrder> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/payments/clubs/${clubId}/initiate`);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to initiate payment');
  }

  return response.json();
}

// Verify Razorpay payment after checkout (signature verification on backend)
export async function verifyClubPayment(data: {
  clubId: string;
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}): Promise<{ success: boolean; message: string }> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/payments/clubs/${data.clubId}/verify`);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      razorpayOrderId: data.razorpayOrderId,
      razorpayPaymentId: data.razorpayPaymentId,
      razorpaySignature: data.razorpaySignature,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Payment verification failed');
  }

  return response.json();
}

// Get current user's club payment history
export async function getMyPayments(): Promise<ClubPayment[]> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl('/api/payments/my');

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch payments');
  }

  return response.json();
}

// Admin: get all payments with optional search
export async function getAllPayments(params?: { search?: string; clubId?: string }): Promise<ClubPayment[]> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.clubId) queryParams.append('clubId', params.clubId);

  const url = buildApiUrl(`/api/payments/admin/all${queryParams.toString() ? `?${queryParams}` : ''}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch all payments');
  }

  // Backend returns { payments: [...], total, page, totalPages }
  const json = await response.json();
  return Array.isArray(json) ? json : (json.payments ?? []);
}
